import { WarningOutlined } from '@ant-design/icons'
import React from 'react'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'
import { accents } from 'sutra/tokens/colors'

export interface StaleFilterBadgeProps {
  /** The subset of this filter's selected values that no longer exist in
   * the project (a renamed/deleted brand, a removed field option). */
  values: readonly string[]
  /** Display label per stale value, when the server has one (falls back to
   * the raw value otherwise — e.g. a deleted theme has no name left). */
  labels: Record<string, string>
  /** `false` drops the hover tooltip (the label stays as `aria-label`). For a
   *  badge sitting inside a trigger that already opens its own hover popup —
   *  two popups side by side for one hover otherwise. Defaults to `true`. */
  withTooltip?: boolean
}

/**
 * Always-visible warning badge marking a filter card as carrying at least
 * one stale value. Read-only — no click target, no toggle — distinct from
 * the ghost (eye-toggled-off) `Disable` affordance: this flags a value the
 * project no longer offers, it doesn't hide anything. The authoritative
 * explanation of consequences (a run failing) lives in `StaleFiltersNotice`;
 * this badge only points at *which* card/value it's about.
 */
export const StaleFilterBadge: React.FC<StaleFilterBadgeProps> = ({
  values,
  labels,
  withTooltip = true,
}) => {
  const displayValues = values.map((v) => labels[v] ?? v)
  const title =
    displayValues.length === 1
      ? `${displayValues[0]} no longer exists in this project.`
      : `${displayValues.join(', ')} no longer exist in this project.`

  const badge = (
    <Badge aria-label={title}>
      <WarningOutlined style={{ fontSize: '13px' }} />
    </Badge>
  )
  if (!withTooltip) return badge
  return <Tooltip content={title}>{badge}</Tooltip>
}

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: ${accents.goldenLight[600]};
  flex-shrink: 0;
`
